import { useEffect, useState, useCallback } from 'react';

const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION;
const CHECK_INTERVAL = 1000 * 60 * 5; // 5 minutes

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkForUpdate = useCallback(async () => {
    // Only check in production to avoid dev server noise
    if (import.meta.env.DEV) return;
    if (updateAvailable) return;

    try {
      // Add a cachebusting query param so we never get a stagnant service worker or browser cache
      const res = await fetch(`/version.json?t=${Date.now()}`);
      
      if (!res.ok) return;
      
      const data = await res.json();
      const serverVersion = data.version;

      if (serverVersion && CURRENT_VERSION && serverVersion !== CURRENT_VERSION) {
        setUpdateAvailable(true);
      }
    } catch (err) {
      // Fail silently, network could be offline
      console.warn("Failed to check for updates");
    }
  }, [updateAvailable]);

  useEffect(() => {
    // Initial check after 10 seconds to not block primary loading
    const initialTimer = setTimeout(checkForUpdate, 10000);
    
    // Regular polling
    const intervalTimer = setInterval(checkForUpdate, CHECK_INTERVAL);

    // Also check when the user switches tabs back to the app
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  const reloadToUpdate = () => {
    // Force reload ignoring cache
    window.location.reload();
  };

  return { updateAvailable, reloadToUpdate };
}
